import { Placement } from "@popperjs/core";
import { useClickAway, useWindowSize } from "@uidotdev/usehooks";
import clsx from "clsx";
import {
  forwardRef,
  isValidElement,
  MutableRefObject,
  RefObject,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";
import { Modifier, usePopper } from "react-popper";
import { useElement } from "@/hooks/common/use-element";
import { DefaultComponentProps, RequireOnlyOne } from "@/interfaces/common";
import { Button, ButtonProps } from "../Button";
import { Icon, IconProps, IconType } from "../Icon";
import { Portal } from "../Portal";

import "./styles.css";

type Action = ButtonProps | React.ReactNode;

interface PopoverListItem {
  label: string;
  children: React.ReactNode;
}

type Props = RequireOnlyOne<
  DefaultComponentProps & {
    placement?: Placement;
    contentClassName?: string;
    actions: [Action, ...Action[]];
    menu?: PopoverListItem[];
    actionIcon?: IconType | React.ReactNode;
    actionButtonProps?: ButtonProps;
    disabled?: boolean;
    nodesToIgnore?: (HTMLElement | null)[];
    alwaysClose?: boolean;
    showToggle?: boolean;
    sameWidth?: boolean;
    onHover?: boolean;
    showPointer?: boolean;
    accessibleContent?: boolean;
  },
  "children" | "actions" | "menu"
>;

export interface PopoverRef {
  close: () => void;
}

export const Popover = forwardRef<any, Props>(function Popover(
  {
    children,
    className,
    contentClassName,
    actions,
    placement = "bottom-end",
    actionIcon,
    actionButtonProps = {},
    menu,
    disabled,
    nodesToIgnore = [],
    alwaysClose,
    showToggle,
    sameWidth,
    showPointer,
    onHover,
    accessibleContent = false,
  },
  forwardedRef,
) {
  const [isOpen, setIsOpen] = useState(false);
  const [attachableRef, setAttachableRef] = useState<HTMLDivElement | null>();
  const [popperElement, setPopperElement] = useState<HTMLDivElement | null>();

  const [ActionIcon] = useElement<Partial<IconProps>>(
    {
      size: "1.5rem",
      className: "pointer-events-none",
    },
    actionIcon ?? <Icon name="MoreHorizontal" />,
  );

  const buttonRef = useRef<HTMLButtonElement>(null);
  const childrenRef = useRef<HTMLDivElement>(null);

  const contentRef: MutableRefObject<HTMLDivElement | null> = useClickAway<HTMLDivElement>((event) => {
    if (shouldIgnore(event, buttonRef, childrenRef, ...nodesToIgnore)) {
      return;
    }

    if (isOpen) {
      setIsOpen(false);
    }
  });

  const modifiers = useMemo<Partial<Modifier<string, object>>[]>(() => {
    if (!sameWidth) {
      return [];
    }

    return [
      {
        name: "sameWidth",
        enabled: true,
        phase: "beforeWrite",
        requires: ["computeStyles"],
        fn({ state }) {
          state.styles.popper.minWidth = `${state.rects.reference.width}px`;
        },
        effect({ state }) {
          state.elements.popper.style.minWidth = `${(state.elements.reference as HTMLElement).offsetWidth}px`;
        },
      },
    ];
  }, []);

  const { styles, attributes } = usePopper(attachableRef, popperElement, {
    placement,
    modifiers,
  });

  const size = useWindowSize();

  const isButton = (item: Action): item is ButtonProps => {
    return !isValidElement(item);
  };

  const shouldIgnore = (event: Event, ...values: (HTMLElement | RefObject<HTMLElement> | null)[]) => {
    return values.some((item) => {
      const node = item instanceof HTMLElement ? item : item?.current;

      return node?.contains(event.target as Node);
    });
  };

  const handleClick = (event: React.BaseSyntheticEvent) => {
    event.stopPropagation();
    setIsOpen(!isOpen);
  };

  const handleEscape = (event: React.KeyboardEvent) => {
    if (event.key === "Escape" && isOpen) {
      event.stopPropagation();
      setIsOpen(false);
      buttonRef.current?.focus();
    }
  };

  const Toggler = useCallback(() => {
    if (!showToggle) {
      return null;
    }

    return (
      <Icon
        name="KeyboardArrowDown"
        className={clsx("transform transition-transform duration-300", {
          "rotate-180": isOpen,
        })}
      />
    );
  }, [isOpen, showToggle]);

  if (Object.keys(forwardedRef ?? {}).length) {
    // biome-ignore lint/correctness/useHookAtTopLevel: <explanation>
    useImperativeHandle<unknown, PopoverRef>(forwardedRef, () => ({
      close: () => setIsOpen(false),
    }));
  }

  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  useEffect(() => {
    if (isOpen) {
      setIsOpen(false);
    }
  }, [size]);

  return (
    <div className={className}>
      <div className="relative flex items-center" ref={setAttachableRef}>
        <Button
          disabled={disabled}
          ref={buttonRef}
          className="flex w-fit cursor-pointer items-center !p-0 font-normal"
          variant="unstyled"
          onClick={onHover ? undefined : handleClick}
          onMouseEnter={onHover ? () => setIsOpen(true) : undefined}
          onMouseLeave={onHover ? () => setIsOpen(false) : undefined}
          trailing={showToggle ? <Toggler /> : undefined}
          {...actionButtonProps}
          aria-expanded={accessibleContent ? isOpen : actionButtonProps["aria-expanded"]}
          onKeyDown={
            accessibleContent
              ? (event) => {
                  actionButtonProps.onKeyDown?.(event);
                  if (!event.defaultPrevented) handleEscape(event);
                }
              : actionButtonProps.onKeyDown
          }
        >
          {actionButtonProps.children ?? (ActionIcon && <ActionIcon />)}
        </Button>

        {isOpen && (
          <Portal>
            <div
              id="popover"
              role={accessibleContent ? undefined : "button"}
              tabIndex={accessibleContent ? undefined : 0}
              ref={(node) => {
                setPopperElement(node);
                contentRef.current = node;
              }}
              {...attributes.popper}
              className={clsx(
                "absolute z-[1000] flex w-auto flex-col gap-2 rounded-md bg-background-primary p-2 shadow-box-primary",
                { "p-0": !!menu?.length, "popper-pointer": showPointer },
                contentClassName,
              )}
              style={styles.popper}
              onMouseEnter={onHover ? () => setIsOpen(true) : undefined}
              onMouseLeave={onHover ? () => setIsOpen(false) : undefined}
              onClick={alwaysClose ? () => setIsOpen(false) : undefined}
              onKeyDown={accessibleContent ? handleEscape : undefined}
            >
              {children ??
                actions?.map((content) =>
                  isButton(content) ? (
                    <Button
                      key={content.children as string}
                      variant="unstyled"
                      {...content}
                      className={clsx("!place-content-start !whitespace-nowrap px-6", content.className)}
                    />
                  ) : (
                    content
                  ),
                ) ??
                (menu?.length && (
                  <ul>
                    {menu?.map(({ label, ...item }) => (
                      <li key={label} className="p-4 first:pt-6 last:pb-6">
                        <Popover
                          placement="auto-start"
                          actionButtonProps={{
                            className: "!min-h-[auto] !p-0",
                            children: (
                              <div className="flex w-full justify-between gap-4">
                                {label}
                                <div>
                                  <Icon name="KeyboardArrowRight" />
                                </div>
                              </div>
                            ),
                          }}
                        >
                          <div ref={childrenRef} className="w-full">
                            {item.children!}
                          </div>
                        </Popover>
                      </li>
                    ))}
                  </ul>
                ))}
            </div>
          </Portal>
        )}
      </div>
    </div>
  );
});
